import React, { useState, useCallback, useEffect } from "react";

// third party
import moment from "moment";
import { connect } from "react-redux";

// components
import { SelectFilter } from "../_commons/SelectFilter";
import { SelectFilterCustom } from "../_commons/SelectFilterCustom";
import Popover from "../_commons/Popover";
import { Loading } from "../_commons/Loading";
import { Paginator } from "../_commons/Paginator";
import Placeholder from "../_commons/Placeholder";

// actions
import { client } from "../../client";
import { store } from "../../store/configureStore";
import { ActionTypes } from "../../actions/_types";
import { GET_BRAND_PUBLISH_LOGS } from "../../graphql/brands";
import { fetchAssociatedBrandLocations } from "../../actions/actions";

// constants
import { CATALOGUE_PLATFORMS_LOGO } from "../../client-config";

const STATUS_LABEL_MAP = {
	ACK_SUCCESS: "Success",
	ACK_FAILED: "Failed",
	SUCCESS: "Success",
	FAILED: "Failed",
	NA: "NA"
};

const ACTION_RENDER_TEXT_MAP = {
	req_to_go_live: "Request to Go Live",
	verify: "Verification",
	pub_del_coord: "Delivery Area Update"
};

const Logs = ({
	associatedBrandLocations,
	brandId,
	locationId,
	isMultibrandEnabled,
	fromLocations,
	selectedBrandLocation,
	brandLocations,
	handleBrandLocation,
	handleBrandsLabelOption,
	showPopover,
	renderPopover,
	handlePopoverClose
}) => {
	const limit = 10;
	const [offset, setOffset] = useState(0);
	const [data, setData] = useState({});
	const [appliedFilters, setAppliedFilters] = useState({});
	const [loading, setLoading] = useState(false);
	const [selectedLocation, setSelectedLocation] = useState();

	const fetchData = useCallback(async () => {
		try {
			setLoading(true);
			const variables = {
				limit,
				offset,
				sort: {
					field: "created",
					order: "DESC"
				},
				brandId: parseInt(brandId)
			};
			if (fromLocations) {
				variables.locationIds = [locationId];
			}
			if (selectedLocation) {
				variables.locationIds = [selectedLocation?.id];
			}
			// sidebar filters
			let filtersObject = [];
			Object.keys(appliedFilters).forEach((f) => {
				if (!appliedFilters[f]) {
					return;
				}
				if (typeof appliedFilters[f] === "object") {
					if (appliedFilters[f].value) {
						filtersObject.push({
							field: f,
							value: appliedFilters[f].value
						});
					}
				} else {
					filtersObject.push({
						field: f,
						value: appliedFilters[f]
					});
				}
			});
			if (filtersObject.length > 0) {
				variables.filters = filtersObject;
			}
			const resp = await client.query({
				query: GET_BRAND_PUBLISH_LOGS,
				variables,
				fetchPolicy: "no-cache"
			});
			if (resp?.data?.locationPlatformActionLogs) {
				setData(resp?.data?.locationPlatformActionLogs);
			}
		} catch (error) {
			console.log(error);
			store.dispatch({
				type: ActionTypes.SHOW_GLOBAL_MESSAGE,
				payload: {
					message: error.message || "Something went wrong.",
					timeout: 2000,
					error: true,
					errObject: error
				}
			});
		}
		setLoading(false);
	}, [selectedLocation, offset, appliedFilters, brandId, locationId, fromLocations]);

	//const loading = false;

	useEffect(() => {
		if (!fromLocations) {
			fetchAssociatedBrandLocations(String(brandId), "");
		}
	}, []);

	useEffect(() => {
		fetchData();
	}, [selectedLocation, appliedFilters, offset, brandId]);

	const setFilter = (field, value) => {
		if (field === "locationId") {
			setSelectedLocation(value ? value : null);
			return;
		}
		setAppliedFilters((current) => ({
			...current,
			[field]: value
		}));
	};
	const handlePagination = (page) => {
		setOffset((page - 1) * 10);
	};

	const actionFilterValues = (data?.filters ?? []).filter((filter) => filter.field === "action");
	const platformFilterValues = (data?.filters ?? []).filter((filter) => filter.field === "platform");

	const placeholderContent = {
		placeholderText: "No logs found!",
		placeholderImageUrl: "/assets/empty_states/graphics-empty-logs.svg",
		redirectionLinkText: "learn more about brands",
		size: "medium"
	};

	return (
		<div className={"brand-logs-tab" + (loading ? " no-click" : "")}>
			<div className="search-container">
				{isMultibrandEnabled && fromLocations && brandLocations.length > 0 && selectedBrandLocation && (
					<Popover
						open={showPopover}
						renderPopover={renderPopover}
						showClose={true}
						onClose={handlePopoverClose}
						position="down-left"
					>
						<SelectFilterCustom
							options={brandLocations || []}
							field="selectedBrandLocation"
							currValue={selectedBrandLocation}
							setFilter={(f, value) => handleBrandLocation(value)}
							labelKey={"brandName"}
							valueKey={"brandId"}
							isSearchable={false}
							customLabel={true}
							customOptions={true}
							isClearable={false}
							renderLabel={handleBrandsLabelOption}
							renderOptions={handleBrandsLabelOption}
							placeholder="Select brand"
						/>
					</Popover>
				)}
				<SelectFilter
					placeholder="Platform"
					options={platformFilterValues?.[0]?.values || []}
					field="platform"
					currValue={appliedFilters.platform || null}
					setFilter={setFilter}
					labelKey="valueForDisplay"
					valueKey="value"
				/>
				{!fromLocations && (
					<SelectFilter
						options={associatedBrandLocations.items || []}
						placeholder="Location"
						currValue={selectedLocation}
						setFilter={setFilter}
						labelKey="name"
						valueKey="id"
						field="locationId"
					/>
				)}
				<SelectFilter
					placeholder="Action"
					options={actionFilterValues?.[0]?.values || []}
					currValue={appliedFilters.action || null}
					setFilter={setFilter}
					labelKey="valueForDisplay"
					valueKey="value"
					field="action"
				/>
			</div>
			{loading ? (
				<div className="P(10px)">
					<div className="shimmer H(60px) Mb(10px)" />
					<div className="shimmer H(60px) Mb(10px)" />
				</div>
			) : (
				<>
					{data?.objects?.length === 0 && !loading && <Placeholder {...placeholderContent} />}
					{data?.objects?.length === 0 && loading && (
						<div className="P(10px 0)">
							<div className="shimmer H(60px) Mb(10px)" />
							<div className="shimmer H(60px) Mb(10px)" />
						</div>
					)}
					{data?.objects?.length !== 0 && loading && <Loading />}
					{data?.objects?.map((log, i) => (
						<Log key={i} {...log} />
					))}
					<Paginator limit={10} offset={offset} count={data?.count || 0} goToPage={handlePagination} />
				</>
			)}
		</div>
	);
};

const mapStateToProps = (store) => ({
	associatedBrandLocations: store.configItems.associatedBrandLocations
});
export default connect(mapStateToProps)(Logs);

const Log = ({ id, platformName, action, finalStatus, user, updated, result, locationPlatform }) => {
	return (
		<div className="publish-log-item">
			<div className="log-header">
				<div className="log-action" title={platformName}>
					<img
						src={
							CATALOGUE_PLATFORMS_LOGO[platformName.toLowerCase()] || "/assets/icons/icons8-globe-40.png"
						}
						alt=""
					/>
					<div>
						<span
							className={`highlight ${
								ACTION_RENDER_TEXT_MAP[action.toLowerCase()] ? "" : "text-capitalize"
							}`}
						>
							{ACTION_RENDER_TEXT_MAP[action.toLowerCase()] || action.toLowerCase()}
						</span>{" "}
						on
						<span className="highlight"> {moment(updated).format("DD MMM, YYYY")}</span> at
						<span className="highlight"> {moment(updated).format("hh:mm A")}</span>
						{user && <span>&nbsp;by&nbsp;</span>}
						{user && <span className="highlight">{user.username}</span>}
					</div>
				</div>
				<div className={"log-status " + finalStatus}>
					<span>{STATUS_LABEL_MAP[finalStatus] || finalStatus || "--"}</span>
				</div>
			</div>
			<div className="log-result">
				<div className="result-text highlight">{result || "--"}</div>
				<div className="ref-id">
					REFERENCE ID: {id} {locationPlatform?.location?.name ? ", " + locationPlatform?.location?.name : ""}
				</div>
			</div>
		</div>
	);
};
